import React, { FC, useCallback, useState } from 'react';
import {
  Box,
  BoxProps,
  CircularProgress,
  Container,
  Grid,
  GridProps,
  makeStyles,
  Theme,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { SubmitButton, U1TextField } from 'components/atoms';
import { Field, Form } from 'react-final-form';
import U1CheckBox from 'components/atoms/U1CheckBox';

import { EmailIcon } from 'svgs';
import { isPhoneInvalid, parsePhoneOnType } from 'utils/phoneHelpers';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import { useFormSender } from '../../../hooks';
import { regulatoryComplianceForm, regulatoryComplianceMobileForm } from '../../../images/consulting-business-services';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginBottom: theme.spacing(20),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(30),
    },
  },
  container: {
    position: 'relative',
  },
  infoBox: {
    padding: theme.spacing(6.6),
    backgroundImage: `url(${regulatoryComplianceMobileForm})`,
    backgroundSize: 'cover',
    backgroundPosition: 'right',
    height: 730,
    borderLeft: '1px solid #656363',
    [theme.breakpoints.up('lg')]: {
      backgroundImage: `url(${regulatoryComplianceForm})`,
    },
  },
  divider: {
    width: 280,
    height: 4,
    backgroundColor: theme.palette.primary.main,
    margin: theme.spacing(0, 0, 10),
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(10, 0),
    },
  },
  title: {
    marginBottom: theme.spacing(4),
    fontWeight: 800,
    fontSize: 32,
    color: theme.palette.primary.main,
    [theme.breakpoints.up('md')]: {
      fontSize: 72,
    },
  },
  text: {
    marginBottom: theme.spacing(8),
    fontWeight: 500,
    fontSize: 16,
    color: `#656363`,
    [theme.breakpoints.up('md')]: {
      fontSize: 23,
    },
  },
  contactInfo: {
    fontWeight: 500,
    color: theme.palette.primary.dark,
    marginLeft: theme.spacing(2),
  },
  formBox: {
    padding: theme.spacing(6.6, 6.6),
    backgroundColor: theme.palette.common.white,
    height: '100%',
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(6.6, 10),
    },
  },
  spinner: {
    color: theme.palette.common.white,
  },
  grid: {
    marginBottom: theme.spacing(4),
    maxWidth: 500,
  },
  submitBtn: {
    marginTop: theme.spacing(2),
    maxWidth: 500,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.common.white,
    minHeight: 48,
    color: theme.palette.primary.main,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: theme.palette.common.white,
    },
  },
  textField: {
    backgroundColor: 'red',
    '& label': {
      color: `#656363`,
    },
    '& div': {
      '& input': {
        border: '1px solid #656363',
      },
    },
  },
}));

const validator = ({ email = '', firstName = '', phone = '' }) => {
  const errors: Record<string, string> = {};

  // Email
  if (!isEmail(email)) errors.email = 'Invalid Email address';
  if (isEmpty(email)) errors.email = 'Required field';

  // First Name
  if (isEmpty(firstName)) errors.firstName = 'Required field';

  // Subject
  if (isPhoneInvalid(phone)) errors.phone = 'Invalid phone number';

  return errors;
};

interface SubmitFormProps {
  withoutDivider?: boolean;
}

const SubmitForm: FC<SubmitFormProps> = ({ withoutDivider }) => {
  const classes = useStyles();
  const {
    onSubmit,
    status: { error, loading, success },
  } = useFormSender('/api/contact-us');
  const isSubmitted = success || loading;
  const fields: Array<{
    label: string;
    name: string;
    additionalFieldParams?: unknown;
    Icon?: unknown;
    multiline?: unknown;
    gridParams?: GridProps;
    wrapperParams?: BoxProps;
  }> = [
    {
      label: 'First Name*',
      name: 'firstName',
      gridParams: {
        md: 6,
        xs: 12,
      },
    },
    {
      label: 'Email*',
      name: 'email',
      gridParams: {
        md: 6,
        xs: 12,
      },
      Icon: {
        startIcon: <EmailIcon />,
        hasIcon: true,
      },
    },
    {
      label: 'Phone Number',
      name: 'phone',
      gridParams: {
        md: 6,
        xs: 12,
      },
      additionalFieldParams: {
        parse: parsePhoneOnType,
      },
    },
  ];
  const [check, setCheck] = useState<boolean>(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const isSmallUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  const handleChange = useCallback(() => {
    setCheck((prevCheck) => !prevCheck);
  }, []);

  const handleSubmit = async (values, actions) => {
    if (check) {
      await onSubmit(values, actions, undefined, {
        agreeToReceive: check ? 'yes' : 'no',
      });
    }
  };

  return (
    <Form
      validate={validator}
      onSubmit={handleSubmit}
      render={({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit}>
          <Box className={classes.root}>
            <Container fixed className={classes.container}>
              {!withoutDivider && <Box className={classes.divider} />}
              <Box className={classes.infoBox}>
                <Grid container>
                  <Grid item lg={7} data-aos="fade-up" data-aos-delay="300">
                    <Typography className={classes.title}>Partner with Us</Typography>
                    <Typography className={classes.text}>
                      Ready to get started? Utilities One’s team is here to help you access available funding and drive your
                      project to success. Reach out today to begin your journey.
                    </Typography>

                    {fields.map(({ label, name, additionalFieldParams, Icon, multiline, wrapperParams }, index) => (
                      <div className={classes.grid} key={index}>
                        <Field name={name} {...additionalFieldParams}>
                          {({ input, meta: { error, touched } }) => (
                            <Box {...wrapperParams}>
                              <U1TextField
                                {...Icon}
                                {...multiline}
                                {...input}
                                disabled={isSubmitted}
                                label={label}
                                helperText={touched && error}
                                error={touched && !!error}
                                className={classes.textField}
                              />
                            </Box>
                          )}
                        </Field>
                      </div>
                    ))}

                    <Box mt={4} />
                    <Box maxWidth={520}>
                      <U1CheckBox
                        text={
                          'By submitting this form you agree to receive text messages.\n' +
                          'We do not share or sell SMS opt-in, or phone numbers for SMS.'
                        }
                        onChange={handleChange}
                        checked={check}
                      />
                      {!check && isSubmitClicked && <Typography color={'error'}>{'Checkbox will be checked'}</Typography>}
                    </Box>
                  </Grid>
                </Grid>
                <Box mt={isSmallUp ? 8 : 4} />
                <SubmitButton
                  onClick={() => setIsSubmitClicked(true)}
                  disabled={submitting || error || loading}
                  error={error}
                  success={success}
                  fullWidth
                  check={check}
                  className={classes.submitBtn}
                >
                  {loading && <CircularProgress size="30px" className={classes.spinner} />}
                  {success && 'Thank you for filling out your information!'}
                  {error && 'An error occurred. Please try again.'}
                  {!loading && !success && !error && 'Get in Touch!'}
                </SubmitButton>
              </Box>
            </Container>
          </Box>
        </form>
      )}
    />
  );
};

export default SubmitForm;
